import { useRoute, useRouteParams } from '#imports'

import type { CMSPageType } from '@backmarket/http-api/src/api-specs-content'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { DISCOUNT } from '~/scopes/discount/routes-names'
import { HOME } from '~/scopes/home/route-names'
import { ROUTES as PLP_ROUTES } from '~/scopes/product-list/route-names'

import { CMS } from '../routes-names'
import {
  CMS_PAGE_TYPE_BY_ROUTE_NAME,
  PAGE_NAME_BY_PLP_SLUG,
} from '../service/content-service.config'

type PageParams = {
  pageType: CMSPageType
  pageName: string
  pageCategory?: string
}

function getBuybackPageName({
  pageName,
  experiments,
}: {
  pageName: string
  experiments: ReturnType<typeof useExperiments>
}) {
  if (pageName !== 'home') return pageName
  const variation = experiments['experiment.homeBuybackPageName']

  if (!variation) return pageName

  return variation === 'noVariation' ? 'home' : variation
}

function getPageNameByRouteName({
  route,
  routeParams,
  experiments,
}: {
  route: ReturnType<typeof useRoute>
  routeParams: ReturnType<typeof useRouteParams>
  experiments: ReturnType<typeof useExperiments>
}) {
  const { pageName } = routeParams

  switch (route.name) {
    case CMS.REFER_FRIEND:
      return 'refer-friend'
    case CMS.STATEMENT:
      return 'accessibility-statement'
    case DISCOUNT.MILITARY:
      return 'military-discount'
    case DISCOUNT.STUDENT:
      return 'student-discount'
    case HOME: {
      const variation = experiments['experiment.homeCMSPageName']

      return variation === 'noVariation' ? 'home' : variation
    }

    case CMS.BUYBACK: {
      return getBuybackPageName({ pageName, experiments })
    }

    case PLP_ROUTES.UNIVERSE: {
      const { market } = useMarketplace()
      const slug = Array.isArray(route.params.slugV2)
        ? route.params.slugV2.join('')
        : route.params.slugV2
      const pageNames = PAGE_NAME_BY_PLP_SLUG[market.countryCode]

      if (!pageNames)
        throw new Error(
          `No available page for this country ${market.countryCode}`,
        )

      return pageNames[slug]
    }
    default:
      return pageName
  }
}

export function usePageParamsFromRoute(): PageParams {
  const route = useRoute()
  const routeName = String(route.name)

  const routeParams = useRouteParams()
  const { pageCategory } = routeParams
  const experiments = useExperiments()

  const pageType = CMS_PAGE_TYPE_BY_ROUTE_NAME[routeName]

  const pageName = getPageNameByRouteName({
    experiments,
    route,
    routeParams,
  })

  return {
    pageType,
    pageName,
    pageCategory,
  }
}
